import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import PlatformLayout from "@/components/PlatformLayout";
import Login from "@/pages/Login";
import RadarPage from "@/pages/RadarPage";
import Signals from "@/pages/Signals";
import Cases from "@/pages/Cases";
import CaseWorkspace from "@/pages/CaseWorkspace";
import Customers from "@/pages/Customers";
import Customer360 from "@/pages/Customer360";
import Impact from "@/pages/Impact";
import Observability from "@/pages/Observability";
import Connectors from "@/pages/Connectors";
import Knowledge from "@/pages/Knowledge";
import Journeys from "@/pages/Journeys";
import JourneyEditor from "@/pages/JourneyEditor";
import Settings from "@/pages/Settings";
import PlatformTenants from "@/pages/PlatformTenants";
import PlatformTenantDetail from "@/pages/PlatformTenantDetail";
import PlatformOnboarding from "@/pages/PlatformOnboarding";
import PlatformAudit from "@/pages/PlatformAudit";

const Loading = () => (
  <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading…</div>
);

/**
 * Route gating.
 *
 * What is gated here is only what gets *rendered*. Authority is resolved from
 * membership on the server for every request, so this cannot grant access —
 * it exists so a viewer is not shown a page that will only 403 at them.
 *
 * `ready` is load-bearing rather than cosmetic: permissions arrive from
 * /auth/me asynchronously, and without waiting, every gated route renders a
 * redirect on first paint and bounces the user home before the answer lands.
 */
const Guard = ({ children, require, platform }) => {
  const { user, permissions, ready, isPlatformAdmin, hasWorkspace } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!ready) return <Loading />;

  if (platform) {
    return isPlatformAdmin ? children : <Navigate to="/" replace />;
  }
  // A staff-only account holds no membership, so there is no workspace to
  // land in. Send them where they can actually do something rather than
  // letting every tenant screen 403 at them in turn.
  if (!hasWorkspace) {
    return isPlatformAdmin ? <Navigate to="/admin" replace /> : <Navigate to="/login" replace />;
  }
  if (require && !require.every((p) => permissions.includes(p))) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* The platform console is a sibling of the tenant app, not a page
              inside it: it has no tenant, so none of Layout's chrome applies. */}
          <Route path="/admin" element={<Guard platform><PlatformLayout /></Guard>}>
            <Route index element={<PlatformTenants />} />
            <Route path="tenants/:id" element={<PlatformTenantDetail />} />
            <Route path="onboarding" element={<PlatformOnboarding />} />
            <Route path="audit" element={<PlatformAudit />} />
          </Route>

          <Route element={<Guard><Layout /></Guard>}>
            <Route index element={<RadarPage />} />
            <Route path="signals" element={<Signals />} />
            <Route path="cases" element={<Cases />} />
            <Route path="cases/:id" element={<CaseWorkspace />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<Customer360 />} />
            <Route path="impact" element={<Impact />} />
            <Route path="observability" element={<Observability />} />
            <Route path="connectors" element={<Connectors />} />
            <Route path="knowledge" element={<Knowledge />} />
            <Route path="journeys" element={<Journeys />} />
            <Route path="journeys/:id" element={<JourneyEditor />} />
            {/* Any of the settings permissions opens the page; each tab then
                checks its own. Requiring all of them would shut an admin out
                of a page where four of the five tabs are theirs. */}
            <Route path="settings"
                   element={<Guard require={["connector.manage"]}><Settings /></Guard>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
